import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, Client, SequencerCatalogEntry, SequencerKey, useClients } from '../lib/api';

export default function Clients() {
  const { data: clients, refetch } = useClients();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h1 className="page-h1 text-xl">Clients</h1>
          <p className="page-sub mt-1">
            Group tenants by customer. Sequencer keys, tenant memberships, and (eventually)
            scoped API tokens are managed per client.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary btn-lg">+ New client</button>
      </header>

      <div className="card overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Contact</th>
              <th className="text-right">Tenants</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(clients || []).map(c => (
              <tr key={c.id}>
                <td className="font-medium">{c.name}</td>
                <td className="font-mono text-xs text-dim">{c.slug}</td>
                <td className="text-xs text-dim">{c.contact_email || '—'}</td>
                <td className="text-right tabular-nums">{c.tenant_count}</td>
                <td className="text-right">
                  <button onClick={() => setEditing(c)} className="btn-ghost btn-sm">Edit</button>
                </td>
              </tr>
            ))}
            {clients && clients.length === 0 && (
              <tr><td colSpan={5}>
                <div className="empty">
                  <div className="text-base">No clients yet.</div>
                  <div className="opacity-70">Tenants without a client appear in the All clients view.</div>
                  <button onClick={() => setShowCreate(true)} className="btn-primary mt-4">+ New client</button>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showCreate && <CreateModal onClose={() => { setShowCreate(false); refetch(); }} />}
      {editing && <EditModal client={editing} onClose={() => { setEditing(null); refetch(); }} />}
    </div>
  );
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setErr(null);
    try {
      await api.createClient({
        name, slug: slug || undefined, contact_email: contact || undefined,
      });
      qc.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal title="New client" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Name" value={name} onChange={setName} placeholder="Acme Corp" />
        <Field label="Slug (URL identifier)" value={slug} onChange={setSlug} placeholder="auto-generated from name" mono />
        <Field label="Contact email (optional)" value={contact} onChange={setContact} type="email" />
        {err && <div className="text-err text-xs">{err}</div>}
        <button disabled={!name || busy} onClick={submit} className="btn-primary">Create</button>
      </div>
    </Modal>
  );
}

function EditModal({ client, onClose }: { client: Client; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(client.name);
  const [slug, setSlug] = useState(client.slug);
  const [contact, setContact] = useState(client.contact_email || '');
  const [notes, setNotes] = useState(client.notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      await api.patchClient(client.id, {
        name, slug, contact_email: contact || undefined, notes: notes || undefined,
      });
      qc.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  async function archive() {
    if (!confirm('Archive this client? Existing tenants stay attached.')) return;
    setBusy(true);
    try {
      await api.patchClient(client.id, { archived: true });
      qc.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal title={`Edit ${client.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        <Field label="Name" value={name} onChange={setName} />
        <Field label="Slug" value={slug} onChange={setSlug} mono />
        <Field label="Contact email" value={contact} onChange={setContact} type="email" />
        <label className="block">
          <div className="text-xs text-dim mb-1">Notes</div>
          <textarea className="input text-xs h-20" value={notes} onChange={e => setNotes(e.target.value)} />
        </label>

        <div className="border-t border-line pt-4">
          <div className="text-xs font-semibold mb-2">Tenants</div>
          <TenantsAssignmentEditor clientId={client.id} />
        </div>

        <div className="border-t border-line pt-4">
          <div className="text-xs font-semibold mb-2">Sequencer API keys</div>
          <SequencerKeysEditor clientId={client.id} />
        </div>

        {err && <div className="text-err text-xs">{err}</div>}
        <div className="flex justify-between gap-2 border-t border-line pt-3">
          <button disabled={busy} onClick={archive} className="btn text-err">Archive</button>
          <button disabled={busy} onClick={save} className="btn-primary">Save</button>
        </div>
      </div>
    </Modal>
  );
}


function TenantsAssignmentEditor({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const [allTenants, setAllTenants] = useState<{ id: string; target_domain: string; ga_upn: string; client_id: string | null; client_slug: string | null; status: string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialSelected, setInitialSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [showOnly, setShowOnly] = useState<'all' | 'unassigned' | 'this' | 'other'>('all');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const tenants = await api.listTenants();
      setAllTenants(tenants as any);
      const initial = new Set<string>(
        tenants.filter((t: any) => t.client_id === clientId).map((t: any) => t.id),
      );
      setSelected(initial);
      setInitialSelected(new Set(initial));
    } catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  const filtered = allTenants.filter(t => {
    if (filter && !(`${t.target_domain} ${t.ga_upn}`.toLowerCase().includes(filter.toLowerCase()))) return false;
    if (showOnly === 'unassigned') return t.client_id === null;
    if (showOnly === 'this') return t.client_id === clientId;
    if (showOnly === 'other') return t.client_id && t.client_id !== clientId;
    return true;
  });

  const toggle = (id: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const dirty = (() => {
    if (selected.size !== initialSelected.size) return true;
    for (const id of selected) if (!initialSelected.has(id)) return true;
    return false;
  })();
  const willAdd = [...selected].filter(id => !initialSelected.has(id)).length;
  const willRemove = [...initialSelected].filter(id => !selected.has(id)).length;

  async function apply() {
    setBusy(true); setErr(null);
    try {
      await api.setClientTenants(clientId, [...selected]);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      await refresh();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2 text-xs">
      <div className="text-dim">
        Check tenants to belong to this client. Unchecking a currently-assigned tenant unassigns it (client becomes blank). Tenants in other clients show their current client and can be moved here.
      </div>

      <div className="flex gap-2 items-center">
        <input
          className="input flex-1"
          placeholder="filter by domain or tenant login…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <select
          className="input w-auto text-xs"
          value={showOnly}
          onChange={e => setShowOnly(e.target.value as any)}
          aria-label="Filter scope"
        >
          <option value="all">all tenants</option>
          <option value="this">in this client</option>
          <option value="unassigned">unassigned</option>
          <option value="other">in other clients</option>
        </select>
      </div>

      <div className="border border-line rounded max-h-64 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="p-4 text-center text-dim">No tenants match.</div>
        )}
        {filtered.map(t => {
          const inThis = t.client_id === clientId;
          const inOther = t.client_id && !inThis;
          const checked = selected.has(t.id);
          return (
            <label
              key={t.id}
              className={`flex items-center gap-2 px-3 py-1.5 border-b border-line/50 cursor-pointer hover:bg-line/30 ${
                checked ? 'bg-run/5' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(t.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="truncate">{t.target_domain}</div>
                <div className="text-dim truncate">{t.ga_upn}</div>
              </div>
              {inOther && (
                <span className="text-warn shrink-0">in {t.client_slug || 'other'}</span>
              )}
              {inThis && (
                <span className="text-ok shrink-0">current</span>
              )}
              <span className="text-dim shrink-0 capitalize">{t.status}</span>
            </label>
          );
        })}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-dim">
          {selected.size} selected
          {dirty && (
            <> &middot;{' '}
              {willAdd > 0 && <span className="text-ok">+{willAdd}</span>}
              {willAdd > 0 && willRemove > 0 && ' / '}
              {willRemove > 0 && <span className="text-err">−{willRemove}</span>}
            </>
          )}
        </span>
        <button
          onClick={apply}
          disabled={!dirty || busy}
          className="btn-primary disabled:opacity-50"
        >
          {busy ? 'saving…' : dirty ? `Apply (${willAdd + willRemove} change${willAdd + willRemove === 1 ? '' : 's'})` : 'No changes'}
        </button>
      </div>
      {err && <div className="text-err">{err}</div>}
    </div>
  );
}


/** Multi-key sequencer editor.
 *
 * Layout: one card per sequencer slug from the catalog. Each card lists
 * every key the workspace owns for that slug (label, last4, default
 * badge, edit/remove buttons), and ends with an "Add another key" form.
 * The first key added auto-promotes itself to default; later additions
 * stay non-default unless the operator opts in.
 */
export function SequencerKeysEditor({ clientId }: { clientId: string }) {
  const [catalog, setCatalog] = useState<SequencerCatalogEntry[]>([]);
  const [keys, setKeys] = useState<SequencerKey[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const [cat, ks] = await Promise.all([
        api.listSequencerCatalog(),
        api.listSequencerKeys(clientId),
      ]);
      setCatalog(cat); setKeys(ks);
    } catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { refresh(); }, [clientId]);

  async function setDefault(keyId: string) {
    setBusy(true); setErr(null);
    try {
      await api.patchSequencerKey(clientId, keyId, { is_default: true });
      await refresh();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function rename(keyId: string, label: string) {
    setBusy(true); setErr(null);
    try {
      await api.patchSequencerKey(clientId, keyId, { label });
      await refresh();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function rotate(keyId: string, apiKey: string) {
    setBusy(true); setErr(null);
    try {
      await api.patchSequencerKey(clientId, keyId, { api_key: apiKey });
      await refresh();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function remove(keyId: string, label: string | null) {
    if (!confirm(
      `Remove "${label || 'this key'}"? Tenants pinned to it will fall back to the workspace default.`
    )) return;
    setBusy(true);
    try { await api.deleteSequencerKeyById(clientId, keyId); await refresh(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-dim">
        Keys are encrypted server-side and never returned in cleartext.
        Add multiple keys per platform if you run more than one account
        (e.g. several Instantly accounts) — give each a label so you can
        route specific tenants to a specific account.
      </div>
      {err && <div className="text-err text-xs">{err}</div>}
      {catalog.map(s => {
        const sKeys = keys.filter(k => k.sequencer === s.slug);
        return (
          <div key={s.slug} className="border border-line rounded p-3 text-xs space-y-2">
            <div>
              <div className="font-semibold text-sm">
                {s.name}{' '}
                <span className="text-dim font-normal">
                  [{s.status === 'live' ? 'live' : 'integration planned'}]
                </span>
              </div>
              <div className="text-dim mt-0.5">{s.description}</div>
            </div>
            {sKeys.length > 0 && (
              <ul className="space-y-1.5">
                {sKeys.map(k => (
                  <KeyRow
                    key={k.id} k={k}
                    busy={busy}
                    onSetDefault={() => setDefault(k.id)}
                    onRename={(lbl) => rename(k.id, lbl)}
                    onRotate={(ak) => rotate(k.id, ak)}
                    onRemove={() => remove(k.id, k.label)}
                  />
                ))}
              </ul>
            )}
            <AddKeyForm
              clientId={clientId} sequencer={s.slug}
              hasExisting={sKeys.length >= 1}
              disabled={s.status !== 'live'}
              onAdded={refresh}
            />
          </div>
        );
      })}
    </div>
  );
}

function KeyRow({
  k, busy, onSetDefault, onRename, onRotate, onRemove,
}: {
  k: SequencerKey; busy: boolean;
  onSetDefault: () => void; onRename: (label: string) => void;
  onRotate: (apiKey: string) => void; onRemove: () => void;
}) {
  const [mode, setMode] = useState<'view' | 'rename' | 'rotate'>('view');
  const [draft, setDraft] = useState('');

  if (mode === 'rename') {
    return (
      <li className="flex gap-2 items-center">
        <input className="input text-xs flex-1" placeholder="Label (e.g. Acme Production)"
          value={draft} onChange={e => setDraft(e.target.value)} />
        <button onClick={() => { onRename(draft); setMode('view'); setDraft(''); }}
          disabled={!draft.trim()} className="btn-primary text-xs">Save</button>
        <button onClick={() => { setMode('view'); setDraft(''); }} className="btn text-xs">Cancel</button>
      </li>
    );
  }
  if (mode === 'rotate') {
    return (
      <li className="flex gap-2 items-center">
        <input className="input font-mono text-xs flex-1" type="password" placeholder="New API key"
          value={draft} onChange={e => setDraft(e.target.value)} />
        <button onClick={() => { onRotate(draft); setMode('view'); setDraft(''); }}
          disabled={!draft.trim()} className="btn-primary text-xs">Rotate</button>
        <button onClick={() => { setMode('view'); setDraft(''); }} className="btn text-xs">Cancel</button>
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium text-ink truncate">
          {k.label || <span className="text-dim italic">(unlabeled)</span>}
        </span>
        <span className="text-dim">…{k.last4}</span>
        {k.is_default && <span className="pill-ok">default</span>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {!k.is_default && (
          <button onClick={onSetDefault} disabled={busy} className="btn text-[11px]">
            Make default
          </button>
        )}
        <button onClick={() => { setMode('rename'); setDraft(k.label || ''); }} disabled={busy} className="btn text-[11px]">
          Rename
        </button>
        <button onClick={() => { setMode('rotate'); setDraft(''); }} disabled={busy} className="btn text-[11px]">
          Rotate
        </button>
        <button onClick={onRemove} disabled={busy} className="btn text-err text-[11px]">
          Remove
        </button>
      </div>
    </li>
  );
}

function AddKeyForm({
  clientId, sequencer, hasExisting, disabled, onAdded,
}: {
  clientId: string; sequencer: string; hasExisting: boolean;
  disabled: boolean; onAdded: () => void;
}) {
  const isSmartlead = sequencer === 'smartlead';
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [label, setLabel] = useState('');
  const [oauthUrl, setOauthUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reset() {
    setApiKey(''); setLabel(''); setOauthUrl(''); setErr(null);
  }

  async function submit() {
    if (!apiKey.trim() || !label.trim()) return;
    if (isSmartlead && !oauthUrl.trim()) return;
    setBusy(true); setErr(null);
    try {
      await api.createSequencerKey(clientId, {
        sequencer, api_key: apiKey, label: label.trim(),
        oauth_url: isSmartlead ? oauthUrl.trim() : undefined,
      });
      reset(); setOpen(false);
      onAdded();
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  if (disabled) {
    return <div className="text-[11px] text-dim italic">Integration coming soon — keys not accepted yet.</div>;
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn text-xs">
        + Add {hasExisting ? 'another key' : 'a key'}
      </button>
    );
  }

  return (
    <div className="space-y-2 pt-1">
      <input className="input font-mono text-xs" type="password"
        placeholder="Paste API key"
        value={apiKey} onChange={e => setApiKey(e.target.value)} />
      <input className="input text-xs"
        placeholder="Label, e.g. 'Acme Production' (required)"
        value={label} onChange={e => setLabel(e.target.value)} />
      {isSmartlead && (
        <div className="space-y-1.5 pt-1">
          <div className="flex items-baseline justify-between gap-2">
            <label className="text-[11px] uppercase tracking-wider text-dim">
              Smartlead OAuth URL <span className="text-err">*</span>
            </label>
            <Link to="/how-to-use#smartlead-oauth-url" className="text-[11px] text-accent hover:underline">
              How to grab this URL →
            </Link>
          </div>
          <textarea
            className="input font-mono text-[11px] h-20"
            placeholder='https://login.microsoftonline.com/.../authorize?client_id=…&state={"user_id":NNNN}'
            value={oauthUrl} onChange={e => setOauthUrl(e.target.value)}
          />
          <p className="text-[11px] text-dim leading-relaxed">
            Paste the full URL from Smartlead's "Add Outlook account"
            popup — Sendepot only reads <code>state.user_id</code>, no
            credentials are involved.
          </p>
        </div>
      )}
      {err && <div className="text-err text-[11px]">{err}</div>}
      <div className="flex gap-2 justify-end">
        <button onClick={() => { setOpen(false); reset(); }} className="btn text-xs">Cancel</button>
        <button onClick={submit}
          disabled={busy || !apiKey.trim() || !label.trim() || (isSmartlead && !oauthUrl.trim())}
          className="btn-primary text-xs">
          {busy ? 'Saving…' : 'Add key'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', placeholder = '', mono = false,
}: any) {
  return (
    <label className="block">
      <div className="text-xs text-dim mb-1">{label}</div>
      <input className={`input ${mono ? 'font-mono text-xs' : ''}`} type={type} placeholder={placeholder}
        value={value} onChange={e => onChange(e.target.value)} />
    </label>
  );
}

function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4"
      onClick={onClose}
    >
      <div
        className={`bg-[#0d0d12] border border-line rounded-lg shadow-modal animate-slide-up ${wide ? 'w-full max-w-[600px] max-h-[88vh] overflow-y-auto' : 'w-full max-w-[420px]'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 py-4 border-b border-line sticky top-0 bg-[#0d0d12] z-10">
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          <button
            onClick={onClose}
            className="text-dim hover:text-ink transition w-7 h-7 grid place-items-center rounded hover:bg-white/[0.05]"
            aria-label="Close"
          >✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
